/**
 * The templates an editor writes themselves — a poster of free text, a typed list of teams, a chosen club's matches or
 * a league's top scorers — rather than one the competition's matches fill in. Their content lives in a stored draft
 * (`socialShareDraft.ts`), and their poster link names that draft instead of a competition.
 */
export type SocialShareDraftTemplate = 'quote' | 'cup-draw' | 'announcement' | 'club-results' | 'goalscorers';

export const SOCIAL_SHARE_DRAFT_TEMPLATES: readonly SocialShareDraftTemplate[] = [
  'quote',
  'cup-draw',
  'announcement',
  'club-results',
  'goalscorers',
];

/** The social-media poster templates, matching the web routes `/social/<template>`. */
export type SocialShareTemplate = 'fixtures' | 'table' | SocialShareDraftTemplate;

export const SOCIAL_SHARE_TEMPLATES: readonly SocialShareTemplate[] = [
  'fixtures',
  'table',
  ...SOCIAL_SHARE_DRAFT_TEMPLATES,
];

export const isDraftTemplate = (template: SocialShareTemplate): template is SocialShareDraftTemplate =>
  (SOCIAL_SHARE_DRAFT_TEMPLATES as readonly SocialShareTemplate[]).includes(template);

/** Poster headline override limit, shared by the admin input and the API DTO. */
export const SOCIAL_SHARE_TITLE_MAX_LENGTH = 40;

/** Poster subtitle limit, shared by the admin input and the API DTO. */
export const SOCIAL_SHARE_SUBTITLE_MAX_LENGTH = 60;

/** Which of a club's names the poster prints: "Felixstowe & Walton United" (full) or "Felixstowe" (short). */
export type SocialShareClubNames = 'full' | 'short';

export const SOCIAL_SHARE_CLUB_NAMES: readonly SocialShareClubNames[] = ['full', 'short'];

export const DEFAULT_SOCIAL_SHARE_CLUB_NAMES: SocialShareClubNames = 'full';

type PosterClub = { fullName?: string; shortName: string };

/**
 * The club's name as the poster prints it. Full by default, as on a printed poster; an editor can pick the short
 * names ("Felixstowe") instead. Either falls back to the other when a club lacks it.
 */
export const socialShareTeamName = (
  team: PosterClub,
  clubNames: SocialShareClubNames = DEFAULT_SOCIAL_SHARE_CLUB_NAMES
): string => (clubNames === 'short' && team.shortName) || team.fullName || team.shortName;

/** What orders a poster's ties: its home club, and its kick-off. */
export type SocialShareTieOrder = { date: string; time?: string; homeTeam: PosterClub };

const byKickOff = (a: SocialShareTieOrder, b: SocialShareTieOrder) =>
  `${a.date} ${a.time ?? ''}`.localeCompare(`${b.date} ${b.time ?? ''}`);

/**
 * A poster's order: A–Z by the home club's name as it prints (full or short), ignoring case; a club at home twice
 * keeps kick-off order. The poster lists its ties this way, and admin shows a split's ties the same way.
 */
export const compareSocialShareTies =
  (clubNames: SocialShareClubNames = DEFAULT_SOCIAL_SHARE_CLUB_NAMES) =>
  (a: SocialShareTieOrder, b: SocialShareTieOrder): number =>
    socialShareTeamName(a.homeTeam, clubNames).localeCompare(socialShareTeamName(b.homeTeam, clubNames), 'en-GB', {
      sensitivity: 'base',
    }) || byKickOff(a, b);

/**
 * What a club name too long for its two lines does: ends in "…" (ellipsis), loses the words that don't fit with no
 * mark (clip), or shrinks until it fits (fit).
 */
export type SocialShareNameOverflow = 'ellipsis' | 'clip' | 'fit';

export const SOCIAL_SHARE_NAME_OVERFLOWS: readonly SocialShareNameOverflow[] = ['ellipsis', 'clip', 'fit'];

export const DEFAULT_SOCIAL_SHARE_NAME_OVERFLOW: SocialShareNameOverflow = 'ellipsis';

/**
 * The image a poster exports as, named by its aspect ratio: 4:5 portrait (the feed post every network shows uncropped),
 * 1:1 square, 9:16 story and 16:9 landscape.
 */
export type SocialShareFormat = '4x5' | '1x1' | '9x16' | '16x9';

export const SOCIAL_SHARE_FORMATS: readonly SocialShareFormat[] = ['4x5', '1x1', '9x16', '16x9'];

export const DEFAULT_SOCIAL_SHARE_FORMAT: SocialShareFormat = '4x5';

/** Each format's canvas in CSS pixels — the PNG's size, since the capture runs at a device scale factor of 1. */
export const SOCIAL_SHARE_FORMAT_SIZES: Readonly<Record<SocialShareFormat, { width: number; height: number }>> = {
  '4x5': { width: 1080, height: 1350 },
  '1x1': { width: 1080, height: 1080 },
  '9x16': { width: 1080, height: 1920 },
  '16x9': { width: 1920, height: 1080 },
};

/** The most images one fixtures poster can be split into. */
export const SOCIAL_SHARE_MAX_PARTS = 4;

/** The most ties one image can name — far above any division's round or day. */
export const SOCIAL_SHARE_MAX_TIES = 100;

/** Joins an image's match ids in its poster link: `ties=<id>,<id>`. */
export const SOCIAL_SHARE_TIES_SEPARATOR = ',';

/** How many ties each of `parts` images gets: as even as it goes, the bigger ones first — 11 in 2 → [6, 5]. */
export const socialSharePartSizes = (count: number, parts: number): number[] =>
  Array.from({ length: parts }, (_, index) => Math.floor(count / parts) + (index < count % parts ? 1 : 0));

/** The ties image `part` (1-based) of `parts` shows, in the order they're given. */
export const socialSharePart = <T>(items: readonly T[], part: number, parts: number): T[] => {
  const sizes = socialSharePartSizes(items.length, parts);
  const start = sizes.slice(0, part - 1).reduce((sum, size) => sum + size, 0);
  return items.slice(start, start + (sizes[part - 1] ?? 0));
};

/** A tenant id this short is already the league's initials ("npl" → "NPL"); a longer one is a word ("Isthmian"). */
const INITIALS_MAX_LENGTH = 4;

/**
 * The league's short name: the tenant's `settings.leagueShortName` when set (WNL), else one made from its id —
 * "npl" → "NPL", "isthmian" → "Isthmian".
 */
export const socialShareLeagueShortName = (tenantId: string, leagueShortName?: string): string => {
  const configured = leagueShortName?.trim();
  if (configured) {
    return configured;
  }
  return tenantId.length <= INITIALS_MAX_LENGTH
    ? tenantId.toUpperCase()
    : `${tenantId.charAt(0).toUpperCase()}${tenantId.slice(1)}`;
};

/**
 * The line under the poster headline when the editor doesn't write one: "NPL Premier Division". A competition whose
 * name already carries the league's short name ("The FA WNL Cup") is printed as it is.
 */
export const defaultSocialShareSubtitle = (leagueShortName: string, competitionName: string): string => {
  const competition = competitionName.trim();
  if (!competition) {
    return leagueShortName;
  }
  const words = competition.toLowerCase().split(/\s+/);
  if (!leagueShortName || words.includes(leagueShortName.toLowerCase())) {
    return competition;
  }
  return `${leagueShortName} ${competition}`;
};

export type SocialSharePathParams = {
  template: SocialShareTemplate;
  /** Fixtures and table: the competition. A draft template has none — its draft holds everything. */
  competitionId?: string;
  /**
   * `YYYY-MM-DD`. Fixtures: the day to show (absent → the whole `round`). Table: the day the standings are as of —
   * always present.
   */
  date?: string;
  /** Fixtures: the round to show in full (when there's no `date`). Table: unused by the poster. */
  round?: number;
  title?: string;
  /** The line under the headline; without one the poster prints `defaultSocialShareSubtitle`. */
  subtitle?: string;
  /** Absent → full names. */
  clubNames?: SocialShareClubNames;
  /** Absent → an ellipsis. */
  nameOverflow?: SocialShareNameOverflow;
  /** Absent → 4:5. */
  format?: SocialShareFormat;
  /** Fixtures: how many images the ties are split across. Absent (or 1) → one image with every tie. */
  parts?: number;
  /** Fixtures: which of the `parts` images this is, from 1. */
  part?: number;
  /**
   * Fixtures: the match ids this image shows, as the editor shared them out. Absent → the poster splits its ties
   * evenly by `parts` and `part`.
   */
  ties?: string[];
  /**
   * The stored draft the poster reads: a draft template's whole content, or a fixtures or table poster's background
   * photo. Admin's `edit` flag is not a parameter here — its iframe appends that itself.
   */
  draft?: string;
};

// Defaults stay out of the link, so a poster made without these options keeps the link it always had.
const setFormat = (params: URLSearchParams, format?: SocialShareFormat): void => {
  if (format && format !== DEFAULT_SOCIAL_SHARE_FORMAT) {
    params.set('format', format);
  }
};

const setParts = (params: URLSearchParams, parts?: number, part?: number): void => {
  if (parts !== undefined && parts > 1) {
    params.set('parts', String(parts));
    params.set('part', String(part ?? 1));
  }
};

/** A draft poster's link: the draft, then the options every poster has. */
const draftPosterParams = ({ draft, format, parts, part }: SocialSharePathParams): URLSearchParams => {
  const params = new URLSearchParams();
  if (draft) {
    params.set('draft', draft);
  }
  setFormat(params, format);
  setParts(params, parts, part);
  return params;
};

/** A fixtures or table poster's link. A background draft, when there is one, goes last. */
const competitionPosterParams = ({
  competitionId,
  date,
  round,
  title,
  subtitle,
  clubNames,
  nameOverflow,
  format,
  parts,
  part,
  ties,
  draft,
}: SocialSharePathParams): URLSearchParams => {
  const params = new URLSearchParams();
  if (competitionId !== undefined) {
    params.set('competition', competitionId);
  }
  if (date) {
    params.set('date', date);
  }
  if (round !== undefined) {
    params.set('round', String(round));
  }
  const headline = title?.trim();
  if (headline) {
    params.set('title', headline);
  }
  const subline = subtitle?.trim();
  if (subline) {
    params.set('subtitle', subline);
  }
  if (clubNames && clubNames !== DEFAULT_SOCIAL_SHARE_CLUB_NAMES) {
    params.set('clubNames', clubNames);
  }
  if (nameOverflow && nameOverflow !== DEFAULT_SOCIAL_SHARE_NAME_OVERFLOW) {
    params.set('nameOverflow', nameOverflow);
  }
  setFormat(params, format);
  setParts(params, parts, part);
  if (ties && ties.length > 0) {
    params.set('ties', ties.join(SOCIAL_SHARE_TIES_SEPARATOR));
  }
  if (draft) {
    params.set('draft', draft);
  }
  return params;
};

/**
 * The site-relative poster path. Admin's "Open on site" link and the API's screenshot use this one function, so
 * what an editor opens is exactly what was captured. Imported by subpath (`@constants/socialShare`): the API can't
 * compile the `@constants` barrel.
 *
 * A draft template's link is `/social/<template>?draft=<id>[&format][&parts&part]`; a fixtures or table link keeps
 * the competition options it always had.
 */
export const buildSocialSharePath = (options: SocialSharePathParams): string => {
  const params = isDraftTemplate(options.template) ? draftPosterParams(options) : competitionPosterParams(options);
  const query = params.toString();
  return query ? `/social/${options.template}?${query}` : `/social/${options.template}`;
};
